import {calculateErrorMessage} from 'common/helpers'

import {cleanupAllWalkthroughAttributes, resolveWalkthroughTargetSync} from './walkthroughDom'
import {
  detectOpenModal,
  setPortalPassThrough,
  waitForModalClose,
} from './walkthroughProviderModalHelpers'

type Params = {
  prevChatOpenRef: any
  modalOpenedByWalkthroughRef: any
  modalWatchInProgressRef: any
  setIsWaitingForModal: (v: boolean) => void
  setState: (s: any) => void
  intl: any
}

export function createProviderDomHelpers(params: Params) {
  const {
    prevChatOpenRef,
    modalOpenedByWalkthroughRef,
    modalWatchInProgressRef,
    setIsWaitingForModal,
    setState,
    intl,
  } = params

  function cleanupPreviousStep() {
    try {
      cleanupAllWalkthroughAttributes()
    } catch (e) {
      calculateErrorMessage(e)
      document.querySelectorAll('.walkthrough-target').forEach((el) => {
        el.classList.remove('walkthrough-target')
      })
    }
    const overlays = document.querySelectorAll(
      '[class*="walkthrough-overlay"], [class*="walkthrough-tooltip"], [class*="walkthrough-highlight"]'
    )
    overlays.forEach((overlay) => {
      if (overlay.parentElement?.id !== 'walkthrough-overlay-portal-root') {
        overlay.remove()
      }
    })
  }

  function closeChatIfOpen() {
    try {
      const closePortalBtn = document.querySelector(
        'button[aria-label="Close chatbot portal"]'
      ) as HTMLElement
      if (closePortalBtn) {
        closePortalBtn.click()
        return
      }

      const minimizeBtn = document.querySelector(
        'button[aria-label="Minimize chatbot portal"]'
      ) as HTMLElement
      if (minimizeBtn) {
        minimizeBtn.click()
        return
      }

      const drawer = document.getElementById('kt_drawer_chat')
      const closeBtn = document.getElementById('kt_drawer_chat_close')
      const toggleBtn = document.getElementById('kt_drawer_chat_toggle')

      const isOpen = !!(
        drawer &&
        (drawer.classList.contains('show') ||
          drawer.getAttribute('data-kt-drawer-show') === 'true' ||
          drawer.getBoundingClientRect().width > 0)
      )

      prevChatOpenRef.current = isOpen

      if (isOpen) {
        if (closeBtn && typeof (closeBtn as HTMLElement).click === 'function') {
          ;(closeBtn as HTMLElement).click()
        } else if (toggleBtn && typeof (toggleBtn as HTMLElement).click === 'function') {
          ;(toggleBtn as HTMLElement).click()
        } else if (drawer) {
          drawer.classList.remove('show')
        }
      }
    } catch (err) {
      calculateErrorMessage(err)
    }
  }

  function waitForUserAction(
    target: string,
    action: 'click' | 'hover' | 'input' | 'none',
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    stepId: string
  ): Promise<boolean> {
    return new Promise((resolve) => {
      if (action === 'none') {
        resolve(true)
        return
      }
      const element = resolveWalkthroughTargetSync(target)
      if (!element) {
        resolve(false)
        return
      }

      let eventType: string
      switch (action) {
        case 'click':
          eventType = 'click'
          break
        case 'hover':
          eventType = 'mouseenter'
          break
        case 'input':
          eventType = 'input'
          break
        default:
          eventType = 'click'
      }

      const handler = async () => {
        element.removeEventListener(eventType, handler)

        await new Promise((r) => setTimeout(r, 1200))

        const modalElement = detectOpenModal()
        if (modalElement) {
          if (modalWatchInProgressRef.current || modalOpenedByWalkthroughRef.current) {
            resolve(false)
            return
          }
          modalOpenedByWalkthroughRef.current = true
          setIsWaitingForModal(true)
          setPortalPassThrough(true)
          setState((prev: any) => ({
            ...prev,
            error: intl.formatMessage({
              id: 'WALKTHROUGH_PROVIDER.PAUSED',
            }),
          }))

          try {
            const modalClosed = await waitForModalClose(modalElement, 60000)

            if (modalClosed) {
              setState((prev: any) => ({...prev, error: null}))
            } else {
              setState((prev: any) => ({
                ...prev,
                error: intl.formatMessage({
                  id: 'WALKTHROUGH_PROVIDER.MODAL_STILL_OPEN',
                }),
              }))
            }
          } finally {
            setIsWaitingForModal(false)
            setPortalPassThrough(false)
            try {
              modalOpenedByWalkthroughRef.current = false
            } catch (e) {
              calculateErrorMessage(e)
            }
          }
        }

        if (modalOpenedByWalkthroughRef.current) {
          resolve(false)
          return
        }
        resolve(true)
      }

      element.addEventListener(eventType, handler)
    })
  }

  return {
    cleanupPreviousStep,
    closeChatIfOpen,
    waitForUserAction,
  }
}
